/**
 * AssignReviewerDialog (Story 24.3 — Epic 24: Report Approval Workflow)
 *
 * Centered modal (UI directive §4.4) to assign / change / unassign the reviewer
 * on a report's current approval cycle. Self-contained: fetches assignable
 * teammates (owner/admin) from GET /api/team/members and PATCHes
 * /api/reports/:id/reviewer.
 *
 * The members endpoint is Starter+ gated, so Free-tier / solo owners get an
 * empty list (403) → the dialog shows a graceful "no assignable teammates"
 * message instead of an empty picker.
 */

import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@provenleap/ui";
import { UserPlus } from "@phosphor-icons/react";
import { apiGet, apiPatch } from "../../lib/apiClient";
import type { ReportListItem } from "./ReportTableView";

interface TeamMember {
  userId: string;
  role: "owner" | "admin" | "viewer";
  email: string;
  displayName: string | null;
}

interface AssignReviewerDialogProps {
  report: ReportListItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAssigned: () => void;
}

export function AssignReviewerDialog({
  report,
  open,
  onOpenChange,
  onAssigned,
}: AssignReviewerDialogProps) {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<string>("");

  const { data: members, isLoading } = useQuery<TeamMember[]>({
    queryKey: ["team-members"],
    enabled: open,
    queryFn: async () => {
      const res = await apiGet("/api/team/members");
      if (!res.ok) {
        // Starter+ gate (403) or any failure → treat as "no assignable teammates".
        return [];
      }
      const json = await res.json();
      return json.data as TeamMember[];
    },
  });

  // Reviewers must be owner/admin (mirrors the server role gate).
  const reviewers = (members ?? []).filter(
    (m) => m.role === "owner" || m.role === "admin",
  );

  const mutation = useMutation({
    mutationFn: async (reviewerId: string | null) => {
      if (!report) return;
      const res = await apiPatch(`/api/reports/${report.id}/reviewer`, {
        reviewerId,
      });
      if (!res.ok) {
        const err = await res
          .json()
          .catch(() => ({ detail: "Failed to assign reviewer" }));
        // Prefer the field-level message (RFC 9457 ValidationError envelope)
        // over the generic title/detail.
        throw new Error(
          err.errors?.[0]?.message ??
            err.detail ??
            err.title ??
            "Failed to assign reviewer",
        );
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["reports"] });
      onAssigned();
      onOpenChange(false);
    },
  });

  // On (re)open: seed the picker to the current reviewer and clear any stale
  // error from a previous report's failed save (the dialog is always-mounted).
  const resetMutation = mutation.reset;
  useEffect(() => {
    if (open) {
      setSelected(report?.reviewerId ?? "");
      resetMutation();
    }
  }, [open, report?.reviewerId, resetMutation]);

  // If the preselected reviewer is no longer an assignable owner/admin (e.g.
  // demoted), fall back to Unassigned so the display matches what Save will send.
  const effectiveSelected =
    selected && reviewers.some((m) => m.userId === selected) ? selected : "";

  function handleOpenChange(next: boolean) {
    if (!next && mutation.isPending) return;
    onOpenChange(next);
  }

  const error =
    mutation.error instanceof Error ? mutation.error.message : null;

  return (
    <DialogPrimitive.Root open={open} onOpenChange={handleOpenChange}>
      <AnimatePresence>
        {open && (
          <DialogPrimitive.Portal forceMount>
            <DialogPrimitive.Overlay asChild forceMount>
              <motion.div
                className="fixed inset-0 z-[60] bg-black/35"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
              />
            </DialogPrimitive.Overlay>
            <DialogPrimitive.Content asChild forceMount>
              <motion.div
                className="fixed left-1/2 top-1/2 z-[60] w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-3xl border-[0.5px] border-input bg-background p-8 shadow-lg outline-none"
                initial={{ opacity: 0, scale: 0.95, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 10 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
                data-testid="assign-reviewer-dialog"
              >
                <div className="flex flex-col space-y-3 text-left">
                  <DialogPrimitive.Title className="text-lg font-semibold leading-none tracking-tight">
                    Assign reviewer
                  </DialogPrimitive.Title>
                  <DialogPrimitive.Description className="text-sm text-muted-foreground">
                    {report
                      ? `Choose who reviews "${report.name}". They'll be notified.`
                      : "Choose who reviews this report."}
                  </DialogPrimitive.Description>
                </div>

                <div className="mt-6">
                  {isLoading ? (
                    <p className="text-[13px] text-muted-foreground">Loading teammates…</p>
                  ) : reviewers.length === 0 ? (
                    <p
                      className="text-[13px] text-muted-foreground"
                      data-testid="no-reviewers"
                    >
                      No assignable teammates. Invite an owner or admin teammate
                      (Starter plan and up) to assign reviewers.
                    </p>
                  ) : (
                    <select
                      data-testid="reviewer-select"
                      value={effectiveSelected}
                      onChange={(e) => setSelected(e.target.value)}
                      disabled={mutation.isPending}
                      className="h-11 w-full rounded-2xl bg-background px-3 text-[13px] text-foreground shadow-xs outline-none"
                      style={{ border: "0.5px solid var(--hairline)" }}
                    >
                      <option value="">Unassigned</option>
                      {reviewers.map((m) => (
                        <option key={m.userId} value={m.userId}>
                          {m.displayName ?? m.email}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                {error && (
                  <p className="mt-4 text-[13px] text-destructive" role="alert">
                    {error}
                  </p>
                )}

                <div className="flex justify-end gap-3 mt-6">
                  <Button
                    variant="outline"
                    className="rounded-2xl bg-background cursor-pointer shadow-xs hover:-translate-y-0.5 hover:shadow-sm hover:bg-background transition-all"
                    style={{ border: "0.5px solid var(--hairline)" }}
                    onClick={() => handleOpenChange(false)}
                    disabled={mutation.isPending}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="brand"
                    className="rounded-2xl text-[13px] cursor-pointer"
                    onClick={() =>
                      mutation.mutate(effectiveSelected === "" ? null : effectiveSelected)
                    }
                    disabled={mutation.isPending || reviewers.length === 0}
                    data-testid="confirm-assign-reviewer-btn"
                  >
                    <UserPlus size={16} className="mr-1" />
                    {mutation.isPending ? "Saving…" : "Save"}
                  </Button>
                </div>
              </motion.div>
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        )}
      </AnimatePresence>
    </DialogPrimitive.Root>
  );
}
